/**
 * Where API requests go, and the one function that makes them.
 *
 * `same-origin` means the page's own origin — Vite proxies `/api` to the API
 * in development — so the app works from any address it is opened at: a
 * tunnel URL shared with someone, a phone on the LAN. A full URL still points
 * at an API elsewhere.
 */
const configured = import.meta.env.VITE_API_URL as string | undefined;

export const API_BASE: string =
  configured === "same-origin" ? "" : (configured ?? "http://localhost:3001");

/**
 * The session has expired, or there never was one (M1-8a).
 *
 * A distinct type because the caller's response differs in kind: every other
 * failure is an error state on the screen, this one is "sign in again". The
 * router catches it and redirects (#135).
 */
export class UnauthorizedError extends Error {
  constructor(path: string) {
    super(`Not signed in: ${path || "/"}`);
    this.name = "UnauthorizedError";
  }
}

/**
 * The signed-in user lacks a permission the route needs.
 *
 * Thrown only by the reads that opt into it — a write's 403 is a `WriteResult`
 * rejection to show, and `apiFetch` cannot tell the two apart. A screen that
 * catches this shows the shared no-access state, naming the permission the
 * server named, rather than a failed-to-load error with a status in it.
 */
export class ForbiddenError extends Error {
  readonly permission: string | undefined;

  constructor(path: string, permission?: string) {
    super(`Not allowed: ${path || "/"}`);
    this.name = "ForbiddenError";
    this.permission = permission;
  }
}

/**
 * `fetch`, with the two things every call to this API needs.
 *
 * **`credentials: "include"`**, because the session lives in a cookie and the
 * API is a different origin from the SPA in every deployed environment
 * (`erp.nonp.estelleluxe.in` calling `backend.erp.nonp.estelleluxe.in`). Without it
 * the browser sends no cookie and a signed-in user looks anonymous — and
 * since it is per call, one forgotten option is one route that mysteriously
 * 401s. Hence this wrapper rather than a note in a comment.
 *
 * **401 becomes `UnauthorizedError`**, so no caller has to remember that a
 * 401 is not an ordinary failure.
 */
export async function apiFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
  });
  if (response.status === 401) throw new UnauthorizedError(path);
  return response;
}
